import { message } from 'antd'
import { AccessorialContext, type BaseFreightFormType, type FormActionType, FormActionTypes } from '../types'
import type { BookType, InvoiceDetail, InvoiceTransaction, TransactionRow } from '@/api/useFreight/types'
import type { TransactionDetail } from '../../columnDefs'
import { constructBaseFreightPayload, createOptionsByIdAndName, findProductTypeById } from '../utils'
import { useTransactions } from '@/api/useFreight/useTransactions'
import { useAddFreightLineItemForm } from './useAddFreightLineItemForm'
import { useMemo } from 'react'
import { useMasterMetadata } from '@/api/useMetadata/useMasterMetadata'
import { GridApi } from 'ag-grid-enterprise'

export function useAddBaseFreightForm(
  bookType: BookType,
  actionType: FormActionType,
  order: TransactionRow | InvoiceTransaction | undefined,
  fliDetail: InvoiceDetail | TransactionDetail | undefined,
  gridApi: GridApi,
  handleClose: () => void,
  context?: AccessorialContext,
  isCreditRebillMode?: boolean
) {
  const { form, isOverrideMutationPending, handleFLIOverride, handleCreditRebillFlow, resetForm } =
    useAddFreightLineItemForm<BaseFreightFormType>(bookType, actionType, order, fliDetail, context)
  const { useAddBaseFreightMutation } = useTransactions()

  const { data: productsResp = { product_hierarchy: {} }, isLoading: isProductsLoading } =
    useMasterMetadata('product_options')
  const productOptions = useMemo(
    () => createOptionsByIdAndName(Object.values(productsResp.product_hierarchy).flatMap((arr) => arr ?? [])),
    [productsResp]
  )

  const initialValues = useMemo(() => {
    if (!fliDetail) return
    return {
      base_freight_subtype: fliDetail.subtype === 'Undefined' ? null : fliDetail.subtype,
      product: { label: fliDetail.product_name || '', value: fliDetail.product_id || '' },
      origin: { label: fliDetail.origin_name || '', value: fliDetail.origin_id || '' },
      destination: { label: fliDetail.destination_name || '', value: fliDetail.destination_id || '' },
      bol_number: fliDetail.bol_number || '',
      amount: fliDetail.amount || 0,
      rate: fliDetail.rate || 0,
      uom: fliDetail.uom,
    }
  }, [fliDetail])

  async function handleSubmit() {
    const values = form.getFieldsValue()

    const productGroup = findProductTypeById(productsResp.product_hierarchy || {}, values.product?.value || '')
    try {
      if (isCreditRebillMode) {
        await handleCreditRebillFlow(values, 'base freight', undefined, productGroup)
      } else if (actionType === FormActionTypes.create) {
        const payload = constructBaseFreightPayload(bookType, order, values)
        await useAddBaseFreightMutation.mutateAsync({ body: payload })
        message.success(`Successfully added ${values.base_freight_subtype}.`)
      } else if (actionType === FormActionTypes.edit) {
        await handleFLIOverride(values)
      }
      gridApi?.refreshCells({ force: true })
      gridApi?.resetRowHeights()
    } catch (error) {
      message.error('There was an issue processing your request. Please try again.')
    } finally {
      handleClose()
      resetForm()
    }
  }

  return {
    form,
    bookType,
    productOptions,
    isProductsLoading,
    isBaseFreightMutationPending: useAddBaseFreightMutation.isPending || isOverrideMutationPending,
    handleSubmit,
    initialValues,
    resetForm,
  }
}
